import React from 'react';

import { themeGet } from '@styled-system/theme-get';
import { bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';
import { useTheme } from 'styled-components/macro';

import { Box, Button, Card, Flex, Icon, Link, Text, colorsByAlias, getColor } from '@olist/united';

import Circles from './card-feedback.style';

const getAction = (action) => {
  if (React.isValidElement(action)) {
    return action;
  }

  if (action.href) {
    return (
      <Link data-ref="action" href={action.href} target={action.newWindow ? '_blank' : '_self'}>
        {action.label}
      </Link>
    );
  }

  if (action.onClick) {
    return (
      <Button data-ref="action-button" variation="default" onClick={action.onClick}>
        {action.label}
      </Button>
    );
  }

  return false;
};

const inverter = 'olist.color.neutral.softest';
const cardVariation = {
  alternate: {
    color: 'olist.color.primary.base',
    inverter,
  },
  primary: {
    color: 'olist.color.accent.base',
    inverter,
  },
  accent: {
    color: 'olist.color.informative.base',
    inverter,
  },
  warning: {
    color: 'olist.color.notice.base',
    inverter,
  },
  danger: {
    color: 'olist.color.alert.base',
    inverter,
  },
  shadow: {
    color: 'olist.color.neutral.soft',
    inverter,
  },
  default: {
    color: 'olist.color.neutral.softer',
    inverter,
  },
  link: {
    color: 'olist.color.primary.base',
    inverter,
  },
  text: {
    color: 'olist.color.neutral.stronger',
    inverter,
  },
};

const CardFeedback = ({ action, description, highlight, icon, title, variation, ...props }) => {
  const theme = useTheme();
  const cardColors = cardVariation[variation] || {};

  return (
    <Card data-ref="card" variation={variation} px={4} py={3} {...props}>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex flex={1}>
          {!!icon && highlight && (
            <Circles
              alignSelf="center"
              data-ref="highlight"
              color={themeGet(cardColors.color)({ theme }) || getColor(variation)}
              mr={3}
            >
              {React.isValidElement(icon) ? (
                icon
              ) : (
                <Icon
                  data-ref="icon"
                  name={icon}
                  color={
                    themeGet(cardColors.inverter)({ theme }) || getColor(variation, 'inverter')
                  }
                  size={20}
                />
              )}
            </Circles>
          )}
          {!!icon && !highlight && (
            <Box pt="1px" pr={2}>
              {React.isValidElement(icon) ? (
                icon
              ) : (
                <Icon
                  data-ref="icon"
                  name={icon}
                  color={themeGet(cardColors.color)({ theme }) || getColor(variation)}
                />
              )}
            </Box>
          )}
          <Box flex={1} mr={24}>
            {!!title && (
              <>
                {React.isValidElement(title) ? (
                  title
                ) : (
                  <Text data-ref="title" fontSize="large" fontWeight="bold" variation={variation}>
                    {title}
                  </Text>
                )}
              </>
            )}

            {!!description && (
              <>
                {React.isValidElement(description) ? (
                  description
                ) : (
                  <Text
                    data-ref="description"
                    color={themeGet('olist.color.neutral.base')({ theme })}
                  >
                    {description}
                  </Text>
                )}
              </>
            )}
          </Box>
        </Flex>
        {!!action && getAction(action)}
      </Flex>
    </Card>
  );
};

CardFeedback.defaultProps = {
  action: undefined,
  description: '',
  highlight: false,
  icon: '',
  title: '',
  variation: 'alternate',
};

CardFeedback.propTypes = {
  action: oneOfType([
    node,
    shape({
      href: string,
      label: string,
      newWindow: bool,
    }),
    shape({
      label: string,
      onClick: func,
    }),
  ]),
  description: oneOfType([string, node]),
  highlight: bool,
  icon: oneOfType([string, node]),
  title: oneOfType([string, node]),
  variation: oneOf(Object.keys(colorsByAlias)),
};

export default CardFeedback;
